import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getEvent, updateEvent } from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import CoverImagePicker from "@/components/CoverImagePicker";
import LocationPicker from "@/components/LocationPicker";
import { CATEGORY_KEYS } from "@/lib/categories";

const CATEGORIES = CATEGORY_KEYS;

function nowLocalISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function EditEventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [denied, setDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id || !user?.id) return;
    getEvent(id, user.id).then((e) => {
      if (!e) { setDenied(true); return; }
      if (e.host_id !== user.id) { setDenied(true); return; }
      setEvent({
        ...e,
        date_time: toLocalInput(e.date_time),
        capacity: e.capacity ?? "",
        additional_notes: e.additional_notes || "",
        cover_url: e.cover_url || null,
        latitude: e.latitude ?? null,
        longitude: e.longitude ?? null,
        exact_address: e.exact_address || "",
        rough_area: e.rough_area || "",
      });
    });
  }, [id, user?.id]);

  if (denied) {
    return (
      <div className="bg-paper min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-display text-3xl text-ink">You can't edit this event.</p>
          <Link to="/my-events" className="inline-block mt-6 text-xs uppercase tracking-widest text-ink hover:text-ink">
            ← Back to my events
          </Link>
        </div>
      </div>
    );
  }
  if (!event) {
    return <div className="bg-paper min-h-[60vh] flex items-center justify-center text-ink/50 text-sm uppercase tracking-widest">Loading…</div>;
  }

  const set = (k, v) => setEvent((e) => ({ ...e, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!event.title || !event.date_time || !event.city || !event.description) {
      toast.error("Fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await updateEvent(id, {
        title: event.title.trim(),
        category: event.category,
        date_time: new Date(event.date_time).toISOString(),
        city: event.city.trim(),
        description: event.description.trim(),
        capacity: event.capacity === "" ? null : Number(event.capacity),
        is_private: !!event.is_private,
        requires_approval: !!event.requires_approval,
        additional_notes: event.additional_notes?.trim() || null,
        cover_url: event.cover_url || null,
        latitude: event.latitude ?? null,
        longitude: event.longitude ?? null,
        exact_address: event.exact_address?.trim() || null,
        rough_area: event.rough_area?.trim() || null,
      });
      toast.success("Saved.");
      navigate(`/events/${id}/manage`);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-8 pb-20">
        <Link
          to={`/events/${id}/manage`}
          data-testid="edit-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors mb-8"
        >
          <ArrowLeft size={14} strokeWidth={2} /> Back to manage
        </Link>

        <span className="text-xs uppercase tracking-[0.3em] text-ink">Edit event</span>
        <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3 mb-12 leading-none">
          Make it<br /><span className="text-ink">better.</span>
        </h1>

        <form onSubmit={onSubmit} data-testid="edit-event-form" className="flex flex-col gap-8">
          <Field label="Title">
            <input type="text" required maxLength={120} value={event.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid="edit-title" className="chunky-input" />
          </Field>

          <Field label="Category">
            <select value={event.category} onChange={(e) => set("category", e.target.value)}
              data-testid="edit-category" className="chunky-input bg-paper">
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-paper">{c}</option>)}
            </select>
          </Field>

          <Field label="Date & time">
            <input type="datetime-local" required min={nowLocalISO()} value={event.date_time}
              onChange={(e) => set("date_time", e.target.value)}
              data-testid="edit-datetime" className="chunky-input" />
          </Field>

          <Field label="City / location">
            <input type="text" required maxLength={80} value={event.city}
              onChange={(e) => set("city", e.target.value)}
              data-testid="edit-city" className="chunky-input" />
          </Field>

          <Field label="Pin the spot on the map">
            <LocationPicker
              value={{
                latitude: event.latitude,
                longitude: event.longitude,
                exact_address: event.exact_address,
                rough_area: event.rough_area,
              }}
              onChange={(loc) =>
                setEvent((cur) => ({ ...cur, ...loc }))
              }
              testidPrefix="edit-loc"
            />
          </Field>

          <Field label="Description">
            <textarea required rows={5} maxLength={2000} value={event.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="edit-description" className="chunky-input resize-y" />
          </Field>

          <Field label="Cover image (optional)">
            <CoverImagePicker
              value={event.cover_url}
              onChange={(url) => set("cover_url", url)}
              testidPrefix="edit-cover"
            />
          </Field>

          <Field label="Capacity (optional)">
            <input type="number" min="1" max="9999" value={event.capacity}
              onChange={(e) => set("capacity", e.target.value)}
              placeholder="Leave empty for unlimited"
              data-testid="edit-capacity" className="chunky-input" />
          </Field>

          <Toggle
            label="Private event"
            sublabel="Hidden from the public feed (you share the link)."
            testid="edit-private"
            checked={!!event.is_private}
            onChange={(v) => set("is_private", v)}
          />

          <Toggle
            label="Approval required"
            sublabel="People request to join — you decide who's in."
            testid="edit-approval"
            checked={!!event.requires_approval}
            onChange={(v) => set("requires_approval", v)}
          />

          <Field label="Additional notes (visible to attendees)">
            <textarea rows={4} maxLength={2000} value={event.additional_notes}
              onChange={(e) => set("additional_notes", e.target.value)}
              placeholder="Meeting point, what to bring, parking, etc."
              data-testid="edit-notes" className="chunky-input resize-y" />
          </Field>

          <button type="submit" disabled={submitting} data-testid="edit-submit"
            className="bg-neon text-ink py-5 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-4">
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>

      <style>{`
        .bare-input {
          background: transparent; border: none;
          border-bottom: 1px solid hsl(var(--border));
          color: #f4f4f0; padding: 12px 0; width: 100%;
          font-family: inherit; font-size: 1rem; outline: none;
          transition: border-color 200ms;
        }
        .bare-input:focus { border-bottom-color: #d6ff00; border-bottom-width: 2px; }
        .bare-input::placeholder { color: rgba(244,244,240,0.35); }
        select.bare-input { appearance: none; cursor: pointer; padding-right: 2rem;
          background-image: linear-gradient(45deg, transparent 50%, #d6ff00 50%), linear-gradient(-45deg, transparent 50%, #d6ff00 50%);
          background-position: calc(100% - 14px) 18px, calc(100% - 9px) 18px;
          background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-ink/60">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, sublabel, checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between gap-4 border border-ink/15 p-4 hover:border-ink transition-colors text-left"
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-widest text-ink">{label}</span>
        <span className="text-xs text-ink/55">{sublabel}</span>
      </div>
      <span
        className={`w-11 h-6 shrink-0 relative transition-colors ${
          checked ? "bg-neon" : "bg-border"
        }`}
        aria-hidden
      >
        <span
          className={`absolute top-0.5 ${checked ? "left-[22px]" : "left-0.5"} w-5 h-5 bg-paper transition-all`}
        />
      </span>
    </button>
  );
}
